import React, { memo, useRef, useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ServiceCard from 'Components/ServiceCard/ServiceCard'
import { getProductDetaillAction } from 'redux/actions/productActions'
import './DescriptionCell.css'

/**
 * Componente para mostrar la descripción de un producto y sus servicios relacionados
 */
const DescriptionCell = memo(({ description, productId }) => {
  // Referencias y estado
  const containerRef = useRef(null)
  const isMounted = useRef(true)
  const [isLoading, setIsLoading] = useState(false)
  const [installable, setInstallable] = useState(undefined)
  const [relatedServices, setRelatedServices] = useState([])

  // Redux
  const dispatch = useDispatch()
  const companyId = useSelector(state => state.company.CompanyId)
  const warehouse = useSelector(state => state.company.WarehouseId)
  const cart = useSelector(state => state.cart.cart)

  // Verificar si el producto aún existe en el carrito
  const productExistsInCart = cart.some(item => item.ProductId === productId)

  // Función para obtener detalles del producto
  const fetchProductDetails = useCallback(async () => {
    if (!productId || isLoading || !productExistsInCart) {
      // Si el producto ya no está en el carrito, limpiar los datos de servicio
      if (!productExistsInCart) {
        setInstallable(false)
        setRelatedServices([])
        return
      }
      return
    }

    setIsLoading(true)

    try {
      const payload = {
        CompanyId: companyId,
        WarehouseId: warehouse,
        ProductId: productId,
        GlobalExecution: true,
        PriceListId: null,
      }

      const response = await dispatch(getProductDetaillAction(payload))
      const data = response?.data[0]

      // Solo actualizar el estado si el componente sigue montado y el producto sigue en el carrito
      if (data && isMounted.current && productExistsInCart) {
        const productInstallable =
          data.PIMAttributes?.attributes?.coreDetail?.installable
        const productRelatedServices = data.PIMAttributes?.relatedServices || []

        // Usar un pequeño timeout para suavizar la transición
        setTimeout(() => {
          if (isMounted.current && productExistsInCart) {
            setInstallable(productInstallable)
            setRelatedServices(productRelatedServices)
            setIsLoading(false)
          }
        }, 10)
      } else if (isMounted.current) {
        setIsLoading(false)

        // Si el producto ya no está en el carrito, limpiar datos
        if (!productExistsInCart) {
          setInstallable(false)
          setRelatedServices([])
        }
      }
    } catch (error) {
      console.error('Error al obtener detalles del producto:', error)
      if (isMounted.current) {
        setIsLoading(false)
      }
    }
  }, [
    dispatch,
    companyId,
    warehouse,
    productId,
    isLoading,
    productExistsInCart,
  ])

  // Limpiar datos cuando el producto se elimina del carrito
  useEffect(() => {
    // Si el producto ya no está en el carrito, limpiar los datos de servicio
    if (!productExistsInCart && (installable || relatedServices.length > 0)) {
      setInstallable(false)
      setRelatedServices([])
    }
  }, [cart, productId, productExistsInCart, installable, relatedServices])

  // Obtener datos del producto cuando se monta el componente
  useEffect(() => {
    // Restablecer el estado de montaje
    isMounted.current = true

    // Solo obtener datos si el producto existe en el carrito
    if (productExistsInCart) {
      fetchProductDetails()
    }

    // Limpiar cuando se desmonta
    return () => {
      isMounted.current = false
    }
  }, [productId, productExistsInCart])

  // Determinar si se debe mostrar el ServiceCard
  const shouldRenderServiceCard = Boolean(
    productExistsInCart &&
      installable &&
      Array.isArray(relatedServices) &&
      relatedServices.length > 0 &&
      relatedServices[0]?.productId
  )

  const containerClassName = [
    'product-description-container',
    isLoading ? 'loading' : '',
    shouldRenderServiceCard ? 'with-service' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const serviceContainerClassName = [
    'service-container',
    shouldRenderServiceCard ? 'visible' : 'hidden',
  ].join(' ')

  return (
    <div className={containerClassName} ref={containerRef}>
      <div className="product-description">{description}</div>

      {/* Contenedor con posición y transición para evitar parpadeos */}
      <div className={serviceContainerClassName}>
        {shouldRenderServiceCard && (
          <div id={`service-card-container-${productId}`}>
            <ServiceCard
              services={relatedServices}
              titleStyle={{ fontSize: '12px' }}
            />
          </div>
        )}
      </div>
    </div>
  )
})

// Nombre para depuración
DescriptionCell.displayName = 'DescriptionCell'

export default DescriptionCell
